/**
 * AI-powered template name and description optimization using Claude API
 */

interface OptimizeNameStep1Params {
  templateName: string;
  applicableSites?: string;
}

interface OptimizeNameStep4Params {
  templateName: string;
  applicableSites: string;
  shiftPatternsCount: number;
  daysCovered: string[];
  shiftTypes: string[];
  totalShifts: number;
  totalHours: number;
  autoAssignCount: number;
  fixedCount: number;
  openCount: number;
}

interface OptimizeDescriptionStep1Params {
  templateName: string;
  applicableSites?: string;
  currentDescription?: string;
}

interface OptimizeDescriptionStep4Params {
  templateName: string;
  currentDescription?: string;
  applicableSites: string;
  shiftPatternsCount: number;
  daysCovered: string[];
  shiftTypes: string[];
  totalShifts: number;
  totalHours: number;
  autoAssignCount: number;
  fixedCount: number;
  openCount: number;
  shiftPatternDetails?: string;
}

/**
 * Optimize template name on Step 1 (Basics)
 * Uses basic context: current name and sites
 */
export async function optimizeTemplateNameStep1(params: OptimizeNameStep1Params): Promise<string> {
  const { templateName, applicableSites = 'Organisation-wide' } = params;

  const prompt = `Improve this rota template name to be more clear and descriptive.

Current name: ${templateName}
Sites/departments: ${applicableSites}

Generate a professional template name (max 50 characters) that is:
- Clear and descriptive
- Appropriate for hospitality industry (restaurants, pubs, hotels)
- Easy to identify at a glance
- Uses sentence case

If current name is very generic (like "Test", "Template", or "temp"), suggest
a name based on the sites if available. If current name has some context
(like "Weekend", "Kitchen", or "Bar"), enhance it to be more descriptive
and professional.

Examples of good names:
- "Weekday Kitchen Coverage"
- "Weekend Bar Service - Soho"
- "Full Week FOH Rota"

Return only the suggested name, nothing else.`;

  try {
    const suggestedName = await callClaude(prompt);

    // Ensure it's not too long
    return suggestedName.slice(0, 50);
  } catch (error) {
    console.error('Error optimizing template name:', error);
    throw error;
  }
}

/**
 * Optimize template name on Step 4 (Review)
 * Uses complete template context for better suggestions
 */
export async function optimizeTemplateNameStep4(params: OptimizeNameStep4Params): Promise<string> {
  const {
    templateName,
    applicableSites,
    shiftPatternsCount,
    daysCovered,
    shiftTypes,
    totalShifts,
    totalHours,
    autoAssignCount,
    fixedCount,
    openCount,
  } = params;

  const prompt = `Analyze this complete rota template and suggest an optimal name.

Template data:
- Current name: ${templateName}
- Sites: ${applicableSites}
- Shift patterns configured: ${shiftPatternsCount}
- Days covered: ${daysCovered.join(', ')}
- Shift types included: ${shiftTypes.join(', ')}
- Total shifts per week: ${totalShifts}
- Total hours per week: ${totalHours}
- Staffing methods: ${autoAssignCount} auto-assigned, ${fixedCount} fixed, ${openCount} open

Generate a concise, professional template name (max 50 characters) that clearly
indicates the template's purpose and coverage.

The name should include:
1. Coverage period (Weekday/Weekend/Full Week if clear)
2. Primary department(s) or shift types (Kitchen/Bar/FOH/etc.)
3. Pattern descriptor if relevant (Coverage/Service/Rota)

Use hospitality industry conventions and sentence case.

Examples of good names:
- "Weekday Kitchen Coverage - Mon-Fri"
- "Weekend Bar & FOH Service"
- "Full Week Management Rota"
- "Summer Kitchen - 7 Days"

Return only the suggested name, nothing else.`;

  try {
    const suggestedName = await callClaude(prompt);

    // Ensure it's not too long
    return suggestedName.slice(0, 50);
  } catch (error) {
    console.error('Error optimizing template name:', error);
    throw error;
  }
}

/**
 * Generate or optimize template description on Step 1 (Basics)
 * Uses basic context: template name, sites, and current description
 */
export async function optimizeDescriptionStep1(
  params: OptimizeDescriptionStep1Params,
): Promise<string> {
  const { templateName, applicableSites = 'Organisation-wide', currentDescription } = params;

  const task = currentDescription
    ? 'Improve and expand the current description to be clearer and more helpful'
    : 'Write a new description based on the template name and sites';

  const prompt = `Generate a helpful template description for a hospitality rota template.

Available information:
- Template name: ${templateName}
- Sites/departments: ${applicableSites}
- Current description: ${currentDescription || 'none'}

Task: ${task}

The description should:
1. Explain what this template is for (infer from name and sites)
2. Suggest when it should be used
3. Provide helpful context about scope and applicability

Guidelines:
- 2-3 sentences maximum
- Max 250 characters
- Clear, professional language for hospitality managers
- Be specific and actionable
- Use sentence case

Examples of good descriptions:
- "Weekend coverage template for bar operations at Soho and Camden locations. Use for standard weekend demand outside of special events or bank holidays."
- "Organisation-wide kitchen staffing template for weekday operations. Suitable for standard demand periods with regular staffing requirements."

Return only the description text, no preamble or explanation.`;

  try {
    const suggestedDescription = await callClaude(prompt);

    // Ensure it's not too long (max 300 characters to be safe)
    return suggestedDescription.slice(0, 300);
  } catch (error) {
    console.error('Error optimizing template description:', error);
    throw error;
  }
}

/**
 * Generate or optimize template description on Step 4 (Review)
 * Uses complete template context for comprehensive suggestions
 */
export async function optimizeDescriptionStep4(
  params: OptimizeDescriptionStep4Params,
): Promise<string> {
  const {
    templateName,
    currentDescription,
    applicableSites,
    shiftPatternsCount,
    daysCovered,
    shiftTypes,
    totalShifts,
    totalHours,
    autoAssignCount,
    fixedCount,
    openCount,
    shiftPatternDetails,
  } = params;

  const task = currentDescription
    ? 'Enhance the current description with complete template details'
    : 'Generate a comprehensive description from scratch';

  const prompt = `Write an optimized template description based on complete template configuration.

Template information:
- Name: ${templateName}
- Current description: ${currentDescription || 'none'}
- Sites: ${applicableSites}
- Shift patterns: ${shiftPatternsCount} patterns configured
- Days covered: ${daysCovered.join(', ')}
- Total shifts per week: ${totalShifts}
- Total hours per week: ${totalHours}
- Shift types: ${shiftTypes.join(', ')}
- Staffing: ${autoAssignCount} auto-assigned, ${fixedCount} fixed, ${openCount} open
${shiftPatternDetails ? `\nShift pattern details:\n${shiftPatternDetails}` : ''}

Task: ${task}

Write a helpful description (2-3 sentences, max 300 characters) that:
1. Explains what coverage this template provides (which days, departments, times)
2. When to use this template (demand level, operational situations, time periods)
3. Important requirements or characteristics (skills needed, special settings)

Use clear, professional language appropriate for hospitality managers.
Be specific with details from the shift patterns.

Example output:
"Provides full weekday kitchen coverage with morning prep (2 employees), lunch service (3 employees), and evening service (2-3 employees). Use for standard Mon-Fri operations. Requires employees with Chef or Line cook tags and ensures 11-hour rest periods between shifts."

Return only the description text, no preamble or explanation.`;

  try {
    const suggestedDescription = await callClaude(prompt);

    // Ensure it's not too long (max 300 characters)
    return suggestedDescription.slice(0, 300);
  } catch (error) {
    console.error('Error optimizing template description:', error);
    throw error;
  }
}

/**
 * Generate AI summary for Step 4 (Review)
 * Comprehensive summary using complete template data
 */
interface GenerateSummaryParams {
  templateName: string;
  description?: string;
  applicableSites: string;
  shiftPatterns: Array<{
    name: string;
    days: string[];
    shifts: Array<{
      type: string;
      start: string;
      end: string;
      employeesNeeded: number;
      staffGroups?: string[];
      tags?: string[];
    }>;
  }>;
  totalPatterns: number;
  totalShifts: number;
  totalHours: number;
  daysCovered: string[];
  autoAssignCount: number;
  fixedAssignments: Array<{ employeeName: string }>;
  openShiftCount: number;
  advancedSettings: Record<string, unknown>;
}

export async function generateAISummary(params: GenerateSummaryParams): Promise<string> {
  const {
    templateName,
    description,
    applicableSites,
    shiftPatterns,
    totalPatterns,
    totalShifts,
    totalHours,
    daysCovered,
    autoAssignCount,
    fixedAssignments,
    openShiftCount,
    advancedSettings,
  } = params;

  // Build shift pattern details
  const shiftPatternDetails = shiftPatterns
    .map(
      (pattern, index) => `
Pattern ${index + 1}: ${pattern.name}
- Days: ${pattern.days.join(', ')}
- Shifts: ${pattern.shifts
        .map(
          (shift) =>
            `${shift.type} (${shift.start}-${shift.end}, ${shift.employeesNeeded} employees${
              shift.staffGroups?.length ? `, ${shift.staffGroups.join('/')} staff` : ''
            }${shift.tags?.length ? `, requires ${shift.tags.join('/')}` : ''})`,
        )
        .join('; ')}`,
    )
    .join('\n');

  // Build advanced settings summary
  const enabledSettings = Object.entries(advancedSettings)
    .filter(([, value]) => {
      if (typeof value === 'boolean') return value === true;
      if (typeof value === 'number') return value > 0;
      if (Array.isArray(value)) return value.length > 0;
      return false;
    })
    .map(([key, value]) => `- ${key}: ${value}`)
    .join('\n');

  const prompt = `Generate a comprehensive summary of this rota template for hospitality managers.

Complete template data:
Template name: ${templateName}
Description: ${description || 'none'}
Sites: ${applicableSites}

Shift patterns (${shiftPatterns.length} patterns):
${shiftPatternDetails}

Coverage summary:
- Total shift patterns: ${totalPatterns}
- Total individual shifts: ${totalShifts}
- Total hours per week: ${totalHours}
- Days covered: ${daysCovered.join(', ')}

Staffing breakdown:
- Auto-assigned shifts: ${autoAssignCount}
- Fixed assignments: ${fixedAssignments.map((a) => a.employeeName).join(', ') || 'none'}
- Open shifts: ${openShiftCount}

Advanced settings enabled:
${enabledSettings || 'none'}

Write a clear, comprehensive summary in two parts:

PART 1 - Overview (3-4 sentences):
Explain what coverage this template provides:
- Which days and times are covered
- Which departments/roles are included
- Total staffing requirements (how many employees, total hours)
- Intended use case and when to apply this template

PART 2 - Key features (3-5 bullet points):
Highlight important aspects:
- Significant settings enabled (auto-assign rules, skill requirements, rest periods, etc.)
- Coverage strengths (continuous coverage, peak time staffing, specific day patterns)
- Special configurations (fixed assignments, handover overlaps, specific employee requirements)
- Staffing approach and requirements

Use clear, professional language appropriate for hospitality managers.
Write in present tense.
Be specific and informative - include actual numbers, days, and requirements.
Maximum 350 words total.

Format your response exactly as:
[Overview paragraph]

Key features:
- [Feature 1]
- [Feature 2]
- [Feature 3]
- [Feature 4]
- [Feature 5]

Return only this formatted summary, no preamble.`;

  try {
    const summary = await callClaude(prompt, { maxTokens: 1500 });

    return summary;
  } catch (error) {
    console.error('Error generating AI summary:', error);
    throw error;
  }
}

const ANTHROPIC_MODEL = 'claude-sonnet-4-20250514';

/**
 * Sends a single user prompt to the Claude messages API and returns the
 * trimmed text of the first content block. Throws when the request is not OK.
 */
async function callClaude(prompt: string, options?: { maxTokens?: number }): Promise<string> {
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': import.meta.env.VITE_ANTHROPIC_API_KEY || '',
      'anthropic-version': '2023-06-01',
    },
    body: JSON.stringify({
      model: ANTHROPIC_MODEL,
      max_tokens: options?.maxTokens ?? 1000,
      messages: [{ role: 'user', content: prompt }],
    }),
  });

  if (!response.ok) {
    throw new Error(`API request failed: ${response.statusText}`);
  }

  const data = (await response.json()) as { content: { text: string }[] };
  return data.content[0].text.trim();
}
